<template lang="pug">
div
    sorting-modal(
        v-model="hotnessToSort"
        ref="hotnessSortingModal"
        sorting-field="rank"
        label-field="name"
        :resource="pluralResource"
        @input="handleSort"
    )
    resource-table(
        v-if="shouldRenderTable"
        item-key="id"
        :can-show-admin-resources="showAdminResources"
        :can-manage-own-resources="isSaveEnabled"
        :show-sort-action="isSaveEnabled"
        :headers="headers"
        :items="hotnessData"
        :singular-resource="singularResource"
        :plural-resource="pluralResource"
        :place-holder-item="placeHolderItem"
        :should-save="isFormValid"
        :selected-label="selectedLabel"
        :is-loading="isLoading"
        :affected-count="affectedContacts"
        :show-success-message="showSuccess"
        :show-error-message="showError"
        additional-message="This is an additional space for a message. "
        @create="handleCreate"
        @edit="handleEdit"
        @cancel="handleCancel"
        @selected-item="setSelectedId"
        @clear-messages="clearMessages"
        @save:create="handleSaveCreate"
        @save:edit="handleSaveEdit"
        @sort="openSortModal"
    )
        template(slot="items" slot-scope="props")
            td
                span {{ props.item.rank }}
            td
                editable-text-field(
                    v-model="props.item.name"
                    :is-editing="shouldEdit(props.item)"
                    :rules="[rules.required, rules.noEmpty, rules.maxLength, isUnique]"
                    @update:error="handleError"
                )
            td
                editable-color-picker(
                    v-model="props.item.color" 
                    :is-editing="shouldEdit(props.item)"
                    :rules="[rules.required, rules.noEmpty, isHex]"
                    @update:error="handleError"
                )
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { CONTACTS } from '../config';

import LiondeskDialog from '@/Core/components/LiondeskDialog';
import ResourceTable from './ResourceTable';
import EditableTextField from './EditableTextField';
import EditableDropDown from './EditableDropDown';
import EditableColorPicker from './EditableColorPicker';
import GetSubAccountPermissions from '../mixins/getSubAccountPermissions';
import GetAffectedContacts from '../mixins/getAffectedContacts';
import SortingModal from './SortingModal';

const {
    mapActions: mapHotnessActions,
    mapState: mapHotnessState,
    mapMutations: mapHotnessMutations,
} = createNamespacedHelpers('hotness');

export default {
    name: 'Hotness',
    mixins: [GetSubAccountPermissions, GetAffectedContacts],
    components: {
        LiondeskDialog,
        ResourceTable,
        EditableTextField,
        EditableDropDown,
        EditableColorPicker,
        SortingModal,
    },
    data() {
        return {
            shouldRenderTable: false,
            resourcePermissionsType: CONTACTS.hotness.resourceType,
            selectedId: null,
            editItem: {},
            isFormValid: true,
            singularResource: CONTACTS.hotness.singularResource,
            pluralResource: CONTACTS.hotness.pluralResources,
            headers: CONTACTS.hotness.headers,
            placeHolderItem: CONTACTS.hotness.placeHolderItem,
            rules: {
                required: v => !!v || 'Required',
                noEmpty: v => (v && v.trim().length > 0) || 'Hotness name cannot be empty',
                maxLength: v => (v && v.length <= 45) || 'Hotness name must be equal to or less than 45 characters',
            },
            isDelete: false,
        };
    },
    async mounted() {
        await this.getAllHotness();
        this.shouldRenderTable = true;
        this.clearMessages();
    },
    watch: {
        async selectedId(value) {
            if (value) {
                await this.findTotalContactsByResource({ hotness_id: value });
            }
            this.isFormValid = true;
        },
    },
    computed: {
        ...mapHotnessState([
            'data',
            'hasError', 
            'errorMsg', 
            'hasSuccess', 
            'isLoading',
        ]),
        hotnessData() {
            return this.data;
        },
        hotnessToSort() {
            return this.hotnessData.filter(
                h => !h.disabled_by || (h.disabled_by && h.disabled_by === 'system')
            );
        },
        selectedLabel() {
            const item = this.data.find(i => i.id === this.selectedId);
            return item && item.name;
        },
        showError: {
            get() {
                return this.hasError;
            },
            set(value) {
                this.clearMessages();
                this.clearAffectedContactsMessage();
            },
        },
        showSuccess: {
            get() {
                return this.hasSuccess;
            },
            set(value) {
                this.clearMessages();
                this.clearAffectedContactsMessage();
            },
        },
    },
    methods: {
        ...mapHotnessActions([
            'getAllHotness',
            'updateRankings',
            'saveHotness',
            'updateHotness',
        ]),
        ...mapHotnessMutations(['clearMessages']),
        handleEdit(itemId) {
            this.selectedId = itemId;
            const found = this.data.find(element => element.id === itemId);
            // must use ... operator because normal assigment won't work
            this.editItem = { ...found }; 
        },
        shouldEdit(item) {
            const { id, disabled_by: disabledBy } = item;
            if (this.selectedId === null) return false;
            return this.selectedId === id && !this.isDelete && !disabledBy;
        },
        handleCancel() {
            this.selectedId = null;
            this.isDelete = false;
            this.editItem = {};
        },
        handleCreate() {
            this.selectedId = 0;
        },
        async handleSaveCreate(item) {
            item.name = item.name.trim();
            await this.saveHotness(item);
            await this.getAllHotness();
        },
        async handleSaveEdit(item) {
            item.name = item.name.trim();

            if (this.editItem.name === item.name) { 
                delete item.name;
            }

            this.selectedId = null;
            await this.updateHotness(item);
            await this.getAllHotness();
        },
        handleError(value) {
            // if there is error it throws true
            this.isFormValid = !value;
        },
        setSelectedId(itemId) {
            this.isDelete = true;
            this.selectedId = itemId;
        },
        isHex(colorVal) {
            const pattern = new RegExp('^#([A-Fa-f0-9]{6})$');
            const matchPattern = pattern.test(colorVal);

            if (!matchPattern) return 'Hotness color must be in hex format';
            return true;
        },
        isUnique(nameVal) {
            const disabledBySystem = 'system';
            const nameValNormalized = nameVal.trim().toLowerCase();

            const isDuplicated =
                nameVal &&
                this.data.some(item => {
                    const isNotDisabled = !item.disabled_by;
                    const isDisabled = item.disabled_by && item.disabled_by === disabledBySystem;
                    const isDifferentId = item.id !== this.selectedId;
                    const hasSameName = item.name.trim().toLowerCase() === nameValNormalized;

                    return (isNotDisabled || isDisabled) && hasSameName && isDifferentId;
                });

            return isDuplicated ? 'Hotness name must be unique' : true;
        },
        async handleSort(items) {
            const ids = items.map(item => item.id);
            const payload = {
                ids,
            };
            await this.updateRankings(payload);
            await this.getAllHotness();
        },
        openSortModal() {
            this.$refs.hotnessSortingModal.modal_open();
        },
    },
};
</script>
